// The front door for a deployment that is invite-only. §25d.
//
// **This is what an unapproved visitor gets instead of the board**, and it is
// the page a crawler would arrive at — so it says what the game is rather than
// only asking for a sign-in. The copy is shared with `index.html`'s boot text
// through `src/client/ai/content/landing.ts`; see there for why.
//
// It renders from `/api/session`, never from the flag (§25.11), so a checkout
// with no accounts configured never sees it.
//
// On the kit since U9 (PLAN-UI §28.0, priority 2). The page's ground used to be
// `var(--ui-page, …)`; it is the `Page` frame's now.

import type React from "react";

import { LEDE, SECTIONS, SIGN_IN } from "src/client/ai/content/landing";

import { Btn, Link, Page, PageColumn, PageTitle, Panel, Text, styled } from "./kit";

/** Why a sign-in bounced, as the callback's `?signin=` says it. */
const REASONS: Record<string, string> = {
  expired: "That sign-in took too long or was interrupted. Please try again.",
  failed: "Google could not complete that sign-in. Please try again.",
  unverified:
    "That Google account's email address is not verified. Verify it with " +
    "Google and try again — we use their check rather than sending you " +
    "another one."
};

export interface LandingProps {
  /** Signed in, but waiting for somebody to approve the account. */
  pending: boolean;
  /** The address we know, shown so a person can tell which account they used. */
  email: string | null;
  /** `?signin=` off the callback, if it sent one. */
  reason?: string | null;
  onPrivacy?: () => void;
  onTerms?: () => void;
  /** Whether the deployment can email. Reported by `/api/session`, never assumed. */
  mail?: boolean;
}

/** The door's plate: the one raised surface on a page of prose. */
const Door = styled(Panel)(({ theme }) => ({
  marginTop: theme.kit.space.xl,
  padding: theme.kit.space.lg
}));

const Section = styled("section")(({ theme }) => ({ marginTop: theme.kit.space.xl }));

const Heading = styled("h2")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 18,
  margin: `0 0 ${theme.kit.space.sm}px`,
  color: theme.kit.color.ink
}));

const Para = styled("p")(({ theme }) => ({
  margin: `${theme.kit.space.sm}px 0 0`,
  color: theme.kit.color.inkDim,
  fontFamily: theme.kit.font.body,
  fontSize: 14,
  lineHeight: 1.6
}));

/**
 * A link that opens one of the public documents. Both are reachable signed out.
 *
 * A button wearing `Link`'s skin: it changes what this app is showing rather
 * than going anywhere, so it may not be an anchor with nothing in its `href`.
 */
const DocButton = styled(Link.withComponent("button"))({
  background: "none",
  border: 0,
  padding: 0,
  font: "inherit",
  cursor: "pointer"
});

/** The sign-in, which is a navigation to Google and so has to be an anchor. */
const BtnLink = styled(Btn.withComponent("a"))({ textDecoration: "none" });

function DocLink({
  onClick,
  testid,
  children
}: {
  onClick?: () => void;
  testid: string;
  children: React.ReactNode;
}): React.JSX.Element | null {
  if (!onClick) return null;
  return (
    <DocButton type="button" onClick={onClick} data-testid={testid}>
      {children}
    </DocButton>
  );
}

/**
 * The Google door on its own: sign in, the pending state, why a sign-in
 * bounced. The landing page's plate, and the second way in on `GatePage`
 * beside the site password.
 */
export function SignInDoor({
  pending,
  email,
  reason,
  onPrivacy,
  onTerms,
  mail
}: LandingProps): React.JSX.Element {
  const note = reason ? REASONS[reason] : null;
  // The door, and its three states. Pending is deliberately not an error: the
  // person did everything right and is waiting on us.
  return (
    <Door solid>
      {pending ? (
        <>
          <Heading data-testid="landing-pending">You are on the list</Heading>
          <Para>
            {email ? <>Signed in as {email}. </> : null}
            Factory Chess is a small invite-only playtest, so somebody has to let you in by
            hand.{" "}
            {/* **Never promise an email the deployment cannot send.** This
                sentence was a promise to a waiting tester that nothing
                kept, so it reads the same `/api/session` fact the privacy
                notice does — switching mail on switches the sentence. */}
            {mail ? (
              <>We will email you the moment that happens — you do not need to keep this page
              open.</>
            ) : (
              <>We are not able to email you yet, so please check back and sign in again — the
              board will simply be there once you have been let in.</>
            )}
          </Para>
          <Btn
            data-testid="landing-signout"
            style={{ marginTop: 18 }}
            onClick={() => {
              void fetch("/api/auth/signout", { method: "POST" }).then(() =>
                window.location.reload()
              );
            }}
          >
            Sign out
          </Btn>
        </>
      ) : (
        <>
          <Heading>Ask for a place in the playtest</Heading>
          <Para>
            Sign in and you join the list. We ask Google for your email address and nothing
            else — not your name, not your photo — and it is used to let you in and to tell
            you when you have been approved.
          </Para>
          {/* A plain link, not a fetch: this is a redirect to Google and
              the browser has to follow it as a navigation. */}
          <BtnLink
            variant="primary"
            data-testid="landing-signin"
            href="/api/auth/google"
            style={{ marginTop: 18 }}
          >
            {SIGN_IN}
          </BtnLink>
          {/* Beside the button rather than buried at the bottom: this is
              the moment somebody is agreeing to something, and both
              documents are readable without signing in. */}
          <Para style={{ fontSize: 12.5 }}>
            Signing in means you accept the{" "}
            <DocLink onClick={onTerms} testid="landing-terms">
              terms of use
            </DocLink>{" "}
            and the{" "}
            <DocLink onClick={onPrivacy} testid="landing-privacy-inline">
              privacy notice
            </DocLink>
            .
          </Para>
        </>
      )}
      {note && (
        <Para data-testid="landing-error" style={{ color: "inherit" }}>
          <Text tone="rust" size={14}>
            {note}
          </Text>
        </Para>
      )}
    </Door>
  );
}

export function LandingPage({
  pending,
  email,
  reason,
  onPrivacy,
  onTerms,
  mail
}: LandingProps): React.JSX.Element {
  return (
    <Page data-testid="landing-page">
      <PageColumn width={720}>
        <PageTitle>Factory Chess</PageTitle>
        <Text tone="ink" block size={17} style={{ lineHeight: 1.55 }}>
          {LEDE}
        </Text>

        <SignInDoor
          pending={pending}
          email={email}
          reason={reason}
          onPrivacy={onPrivacy}
          onTerms={onTerms}
          mail={mail}
        />

        {SECTIONS.map((section) => (
          <Section key={section.heading}>
            <Heading>{section.heading}</Heading>
            <Para>{section.body}</Para>
          </Section>
        ))}

        <Para data-testid="landing-footer" style={{ marginTop: 30, fontSize: 12.5 }}>
          A prototype by <Link href="https://gnarledhelix.com/factory-chess/">Gnarled Helix</Link>.{" "}
          <DocLink onClick={onPrivacy} testid="landing-privacy">
            What we collect
          </DocLink>
        </Para>
      </PageColumn>
    </Page>
  );
}
